import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import './Notifications.css';

interface Notification {
  id: string;
  type: 'rupture' | 'stock_faible' | 'vente' | 'credit' | 'info';
  titre: string;
  message: string;
  temps: Date;
  lu: boolean;
}

const CLE_STOCKAGE = 'nexacom_notifications';

// Chargement initial depuis localStorage (les notifications survivent au refresh).
function chargerDepuisStockage(): Notification[] {
  try {
    const brut = localStorage.getItem(CLE_STOCKAGE);
    if (!brut) return [];
    return (JSON.parse(brut) as Notification[]).map((n) => ({ ...n, temps: new Date(n.temps) }));
  } catch {
    return [];
  }
}

const fmt = (n: number) => Math.round(Number(n)).toLocaleString('fr-FR');

export default function Notifications() {
  const [notifications, setNotifications] = useState<Notification[]>(chargerDepuisStockage);
  const [showPanel, setShowPanel] = useState(false);
  const [nonLues, setNonLues] = useState(0);

  // Fusionne des candidates dans l'état (dédup par id) via mise à jour fonctionnelle
  // -> pas de dépendance sur `notifications`, donc pas de closure obsolète.
  const ajouter = (candidates: Notification[]) => {
    if (candidates.length === 0) return;
    setNotifications((prev) => {
      const connus = new Set(prev.map((n) => n.id));
      const nouvelles = candidates.filter((c) => !connus.has(c.id));
      if (nouvelles.length === 0) return prev;
      return [...nouvelles, ...prev].slice(0, 30);
    });
  };

  const verifierAlertes = useCallback(async () => {
    try {
      const [produitsRes, ventesRes, clientsRes] = await Promise.all([
        api.get('/produits/stats'),
        api.get('/ventes/stats/jour'),
        api.get('/clients').catch(() => ({ data: [] as any[] })),
      ]);

      const maintenant = new Date();
      const jour = maintenant.toLocaleDateString('fr-FR');
      const candidates: Notification[] = [];

      if (produitsRes.data.enRupture > 0) {
        candidates.push({
          id: `rupture-${jour}-${produitsRes.data.enRupture}`,
          type: 'rupture',
          titre: '⚠️ Rupture de stock',
          message: `${produitsRes.data.enRupture} produit${produitsRes.data.enRupture > 1 ? 's' : ''} en rupture de stock`,
          temps: maintenant, lu: false,
        });
      }

      if (produitsRes.data.sousMinimum > 0) {
        candidates.push({
          id: `faible-${jour}-${produitsRes.data.sousMinimum}`,
          type: 'stock_faible',
          titre: '📉 Stock faible',
          message: `${produitsRes.data.sousMinimum} produit${produitsRes.data.sousMinimum > 1 ? 's' : ''} sous le stock minimum`,
          temps: maintenant, lu: false,
        });
      }

      // Crédits clients en cours (solde débiteur).
      const clients = Array.isArray(clientsRes.data) ? clientsRes.data : [];
      const debiteurs = clients.filter((c: any) => Number(c.soldeCredit) > 0);
      const totalCredit = debiteurs.reduce((s: number, c: any) => s + Number(c.soldeCredit), 0);
      if (debiteurs.length > 0) {
        candidates.push({
          id: `credit-${jour}`,
          type: 'credit',
          titre: '💳 Crédits clients en cours',
          message: `${debiteurs.length} client${debiteurs.length > 1 ? 's' : ''} à crédit — total dû : ${fmt(totalCredit)} FCFA`,
          temps: maintenant, lu: false,
        });
      }

      if (ventesRes.data.nombreVentes > 0) {
        candidates.push({
          id: `ventes-${jour}`,
          type: 'vente',
          titre: '✅ Activité du jour',
          message: `${ventesRes.data.nombreVentes} vente${ventesRes.data.nombreVentes > 1 ? 's' : ''} — CA : ${fmt(ventesRes.data.chiffreAffaires)} FCFA`,
          temps: maintenant, lu: false,
        });
      }

      ajouter(candidates);
    } catch (e) {}
  }, []);

  useEffect(() => {
    verifierAlertes();
    const interval = setInterval(verifierAlertes, 60000); // toutes les 60 secondes
    return () => clearInterval(interval);
  }, [verifierAlertes]);

  // Persistance + compteur de non-lues à chaque changement.
  useEffect(() => {
    setNonLues(notifications.filter(n => !n.lu).length);
    try { localStorage.setItem(CLE_STOCKAGE, JSON.stringify(notifications)); } catch {}
  }, [notifications]);

  const marquerToutesLues = () => {
    setNotifications(prev => prev.map(n => ({ ...n, lu: true })));
  };

  const supprimerNotification = (id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const couleurType = (type: string) => {
    switch (type) {
      case 'rupture': return '#A32D2D';
      case 'stock_faible': return '#854F0B';
      case 'vente': return '#3B6D11';
      case 'credit': return '#7A3E9D';
      default: return '#185FA5';
    }
  };

  const tempsRelatif = (date: Date) => {
    const diff = Math.floor((Date.now() - date.getTime()) / 1000);
    if (diff < 60) return 'À l\'instant';
    if (diff < 3600) return `Il y a ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `Il y a ${Math.floor(diff / 3600)}h`;
    return date.toLocaleDateString('fr-FR');
  };

  return (
    <div className="notif-container">
      {/* CLOCHE */}
      <div className="notif-cloche" onClick={() => setShowPanel(!showPanel)}>
        <span>🔔</span>
        {nonLues > 0 && (
          <span className="notif-badge">{nonLues > 9 ? '9+' : nonLues}</span>
        )}
      </div>

      {/* PANNEAU */}
      {showPanel && (
        <>
          <div className="notif-overlay" onClick={() => setShowPanel(false)} />
          <div className="notif-panel">
            <div className="notif-panel-header">
              <span>Notifications</span>
              <div className="notif-header-actions">
                {nonLues > 0 && (
                  <button onClick={marquerToutesLues}>
                    Tout marquer lu
                  </button>
                )}
                <button onClick={() => setShowPanel(false)}>×</button>
              </div>
            </div>

            <div className="notif-liste">
              {notifications.length === 0 ? (
                <div className="notif-vide">
                  <div>🔔</div>
                  <p>Aucune notification</p>
                </div>
              ) : (
                notifications.map(n => (
                  <div key={n.id}
                    className={`notif-item ${!n.lu ? 'non-lu' : ''}`}
                    style={{ borderLeftColor: couleurType(n.type) }}>
                    <div className="notif-content">
                      <div className="notif-titre">{n.titre}</div>
                      <div className="notif-message">{n.message}</div>
                      <div className="notif-temps">{tempsRelatif(n.temps)}</div>
                    </div>
                    <button
                      className="notif-supprimer"
                      onClick={() => supprimerNotification(n.id)}>
                      ×
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}